import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from './Layout';
import SessionDetailModal from './SessionDetailModal';
import ConsultationCompletionForm from './ConsultationCompletionForm';
import ReferralVerification from '../../components/ReferralVerification';

import { userAPI, doctorAPI, sessionAPI, assessmentAPI } from '../../services/api';
import { isSessionJoinable } from '../../utils/sessionTimeUtils';

// Skeleton Loader Components
const MetricCardSkeleton = () => (
  <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-gray-50">
    {/* Icon Skeleton */}
    <div className="w-fit mb-3">
      <div className="w-9 h-9 bg-gray-200 rounded-lg animate-pulse"></div>
    </div>
    {/* Metric Value Skeleton */}
    <div className="h-8 bg-gray-200 rounded w-16 animate-pulse mb-2"></div>
    {/* Label Skeleton */}
    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
  </div>
);

const UrgentCaseSkeleton = () => (
  <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 animate-pulse">
    <div className="flex justify-between items-center mb-2">
      <div className="h-4 bg-gray-200 rounded w-20"></div>
      <div className="h-3 bg-gray-200 rounded w-16"></div>
    </div>
    <div className="h-3 bg-gray-200 rounded w-16 mb-3"></div>
    <div className="h-10 bg-gray-200 rounded w-full"></div>
  </div>
);

const PendingAssessmentSkeleton = () => (
  <div className="p-4 bg-white rounded-lg border border-gray-200 animate-pulse">
    <div className="flex items-start justify-between mb-2">
      <div className="flex gap-4 items-center">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-3 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="h-6 bg-gray-200 rounded w-16"></div>
    </div>
    <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
    <div className="h-10 bg-gray-200 rounded w-full"></div>
  </div>
);

const PsychiatristDashboard = () => {
 
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
const [doctorName, setDoctorName] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    sessionsToday: 0,
    pendingResponse: 0,
    avgSessionLength: 0,
    activePatients: 0,
  });
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [urgentCases, setUrgentCases] = useState([]);
  const [pendingAssessments, setPendingAssessments] = useState([]);
  const [loadingUrgent, setLoadingUrgent] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [showAllPending, setShowAllPending] = useState(false);
  const [startingSession, setStartingSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [selectedSessionForCompletion, setSelectedSessionForCompletion] = useState(null);
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'scheduled': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const [loginDate, setLoginDate] = useState('');

  // Handle session start
  const handleStartSession = async (sessionId, session) => {
    try {
      setStartingSession(sessionId);

      // Update session status to in_progress
      await sessionAPI.updateSessionStatus(sessionId, 'in_progress');

      // If session has Zoom URL, open it in new tab
      if (session.zoomStartUrl) {
        window.open(session.zoomStartUrl, '_blank', 'noopener,noreferrer');
      }

      // Refresh the schedule to show updated status
      await fetchTodaySchedule();

    } catch (error) {
      console.error('Failed to start session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setStartingSession(null);
    }
  };

  // Handle session completion (old method - replaced by completion form)
  const handleCompleteSession = async (sessionId) => {
    try {
      setStartingSession(sessionId);

      // Update session status to completed
      await sessionAPI.updateSessionStatus(sessionId, 'completed');

      // Refresh the schedule
      await fetchTodaySchedule();

    } catch (error) {
      console.error('Failed to complete session:', error);
      alert('Failed to complete session. Please try again.');
    } finally {
      setStartingSession(null);
    }
  };

  // Handle opening consultation completion form
  const handleOpenCompletionForm = (session) => {
    setSelectedSessionForCompletion(session);
    setShowCompletionForm(true);
  };

  // Handle consultation completion form submission
  const handleConsultationComplete = async (formData) => {
    try {
      await sessionAPI.completeConsultation(formData.sessionId, formData);
      
      alert('Consultation completed successfully! Medicare receipt has been generated and sent to the patient.');
      
      // Close the form and refresh the schedule
      setShowCompletionForm(false);
      setSelectedSessionForCompletion(null);
      await fetchTodaySchedule();
      
    } catch (error) {
      console.error('Failed to complete consultation:', error);
      alert('Failed to complete consultation. Please try again.');
    }
  };

  // Fetch today's schedule
  const fetchTodaySchedule = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user) return;

      const data = await doctorAPI.getTodaySessionsByDoctorId(user.id || user._id);
      const mapped = (data.data || [])
        .map(session => ({
          id: session.id,
          time: session.startTime ? session.startTime.slice(0, 5) : '',
          patient: session.patient || { name: 'Unknown', email: '', profileImage: '' }, // Keep full patient object
          patientName: session.patient?.name || 'Unknown', // For display purposes
          type: session.type || 'Session',
          status: session.status || 'scheduled',
          duration: session.duration ? `${session.duration} mins` : '',
          zoomStartUrl: session.zoomStartUrl,
          zoomJoinUrl: session.zoomJoinUrl,
          date: session.date,
          startTime: session.startTime,
          endTime: session.endTime,
        }));
      setTodaySchedule(mapped);
      setLoadingSchedule(false);
    } catch (error) {
      console.error('Failed to fetch today schedule:', error);
      setLoadingSchedule(false);
    }
  };

useEffect(() => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      setDoctorName(user.name); 
      
      // If loginDate doesn't exist, create it
      if (!user.loginDate) {
        const loginDate = new Date().toLocaleDateString('en-IN', {
          weekday: 'short', // This gives 3-letter day (Mon, Tue, etc.)
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Update localStorage with the new date
        const updatedUser = { ...user, loginDate };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setLoginDate(loginDate);
      } else {
        setLoginDate(user.loginDate);
      }
      // Fetch metrics
      doctorAPI.getDashboardMetrics(user.id || user._id)
        .then(data => {
          setDashboardMetrics(data);
          setLoadingMetrics(false);
        })
        .catch(() => setLoadingMetrics(false));
      // Fetch today's schedule
        fetchTodaySchedule();
      // Fetch urgent cases (high risk)
      setLoadingUrgent(true);
      assessmentAPI.getAssessmentsForDoctor(user.id || user._id, { risk: 'High' })
        .then(data => {
          setUrgentCases(data.assessments || []);
          setLoadingUrgent(false);
        })
        .catch(() => setLoadingUrgent(false));
      // Fetch pending assessments (submitted)
      setLoadingPending(true);
      assessmentAPI.getAssessmentsForDoctor(user.id || user._id, { status: 'submitted' })
        .then(data => {
          setPendingAssessments(data.assessments || []);
          setLoadingPending(false);
        })
        .catch(() => setLoadingPending(false));
    } catch (err) {
      console.error('Failed to parse user from localStorage:', err);
    }
  }
}, []);

  // Real-time updates for session status
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update session status and join buttons
      setTodaySchedule(prev => prev.map(session => ({ ...session })));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
 }, []);

  // Helper to format time ago
  const timeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);

    if (diffMs < 1000 * 60 * 60) { // less than 1 hour
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffMs < 1000 * 60 * 60 * 24) { // less than 24 hours
      return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  // Skeleton loader for schedule items
  const ScheduleSkeleton = () => (
    <div className="space-y-3">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg animate-pulse">
          <div className="flex items-center space-x-4">
            <div className="text-center min-w-[60px]">
              <div className="h-4 bg-gray-200 rounded w-12 mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-8"></div>
            </div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-6 bg-gray-200 rounded w-16"></div>
            <div className="h-6 bg-gray-200 rounded w-20"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
       <div className="min-h-screen ">
      {/* Welcome Header */}
      <div className="mb-8">
        <div className=" mb-8">
          <h1 className="text-2xl font-semibold text-gray-900"> Welcome back,  {doctorName || 'Doctor'}</h1>
         <p className="text-gray-600 mt-2">
  {loginDate || 'Login date not available'}
</p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Sessions Today */}
          <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-green-50">
            {/* Icon with background */}
            <div className="w-fit mb-3">
              <span className="bg-green-100 text-green-600 p-2 rounded-lg inline-flex items-center justify-center">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </span>
            </div>
            {/* Metric Value */}
            <p className="text-2xl font-bold text-gray-900">
              {loadingMetrics ? (
                <span className="h-8 bg-gray-200 rounded w-16 inline-block animate-pulse"></span>
              ) : (
                dashboardMetrics.sessionsToday
              )}
            </p>
            {/* Label and Status Row */}
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-gray-500">Sessions Today</span>
            </div>
          </div>

          {/* Pending Response */}
          <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-blue-50">
            <div className="w-fit mb-3">
              <span className="bg-blue-100 text-blue-600 p-2 rounded-lg inline-flex items-center justify-center">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {loadingMetrics ? (
                <span className="h-8 bg-gray-200 rounded w-16 inline-block animate-pulse"></span>
              ) : (
                dashboardMetrics.pendingResponse
              )}
            </p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-gray-500">Pending Response</span>
            </div>
          </div>

          {/* Avg Session Length */}
          <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-yellow-50">
            <div className="w-fit mb-3">
              <span className="bg-yellow-100 text-yellow-600 p-2 rounded-lg inline-flex items-center justify-center">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zM12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                </svg>
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {loadingMetrics ? (
                <span className="h-8 bg-gray-200 rounded w-16 inline-block animate-pulse"></span>
              ) : (
                <>{dashboardMetrics.avgSessionLength} <span className="text-sm font-normal text-gray-600">mins</span></>
              )}
            </p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-gray-500">Avg session length</span>
            </div>
          </div>

          {/* Active Patients */}
          <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-purple-50">
            <div className="w-fit mb-3">
              <span className="bg-purple-100 text-purple-600 p-2 rounded-lg inline-flex items-center justify-center">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.48 1.48 0 0 0 18.54 8H17c-.8 0-1.5.65-1.5 1.45v.55h-3V8.5A1.5 1.5 0 0 0 11 7H9.5A1.48 1.48 0 0 0 8.04 8.37L5.5 16H8v6h4v-6h2v6h6z" />
                </svg>
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {loadingMetrics ? (
                <span className="h-8 bg-gray-200 rounded w-16 inline-block animate-pulse"></span>
              ) : (
                dashboardMetrics.activePatients
              )}
            </p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-gray-500">Active Patients</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Left Side - Today's Schedule */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Today's Schedule</h2>
              {todaySchedule.length > 3 && (
                <button 
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  onClick={() => navigate('/psy/session')}
                >
                  View All
                </button>
              )}
            </div>
            
            <div className="space-y-3">
              {loadingSchedule ? (
                <ScheduleSkeleton />
              ) : todaySchedule.length === 0 ? (
                <div className="text-center text-gray-400 py-8">No sessions scheduled for today.</div>
              ) : (
                todaySchedule.slice(0, 3).map((appointment) => (
                  <div key={appointment.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className="text-center min-w-[60px] flex-shrink-0">
                        <div className="text-sm font-semibold text-gray-900">{appointment.time}</div>
                        <div className="text-xs text-gray-500">{appointment.duration}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{appointment.patientName}</p>
                        <p className="text-sm text-gray-600 truncate">{appointment.type}</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 flex-shrink-0">
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)} whitespace-nowrap`}>
                        {appointment.status === 'in_progress' ? 'In Progress' :
                          appointment.status === 'completed' ? 'Completed' : 'Scheduled'}
                      </span>
                        {appointment.status === 'scheduled' && (
                          (() => {
                            const disabled = startingSession === appointment.id || !isSessionJoinable(appointment);
                            console.log('Doctor Dashboard Start Session button disabled:', disabled, {
                              startingSession,
                              appointmentId: appointment.id,
                              isSessionJoinable: isSessionJoinable(appointment),
                              appointment
                            });
                            return (
                              <button
                                className="text-xs sm:text-sm bg-blue-600 text-white px-2 sm:px-3 py-1 rounded hover:bg-blue-700 whitespace-nowrap w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                style={disabled ? { background: '#ccc', color: '#fff', cursor: 'not-allowed', border: 'none' } : {}}
                                onClick={() => handleStartSession(appointment.id, appointment)}
                                disabled={disabled}
                              >
                                {startingSession === appointment.id ? 'Starting...' : 'Start Session'}
                              </button>
                            );
                          })()
                        )}
                        {appointment.status === 'in_progress' && (
                          <button
                            className="text-xs sm:text-sm bg-green-600 text-white px-2 sm:px-3 py-1 rounded hover:bg-green-700 whitespace-nowrap w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleOpenCompletionForm(appointment)}
                            disabled={startingSession === appointment.id}
                          >
                            {startingSession === appointment.id ? 'Completing...' : 'Complete Consultation'}
                          </button>
                        )}
                       
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Pending Assessments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Pending Assessments</h2>
              {!loadingPending && pendingAssessments.length > 3 && (
                <button
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  onClick={() => setShowAllPending(!showAllPending)}
                >
                  {showAllPending ? 'Show Less' : 'View All'}
                </button>
              )}
            </div>
            
            <div className="space-y-3">
              {loadingPending ? (
                <>
                  <PendingAssessmentSkeleton />
                  <PendingAssessmentSkeleton />
                  <PendingAssessmentSkeleton />
                </>
              ) : pendingAssessments.length === 0 ? (
                <div className="text-gray-500">No pending assessments.</div>
              ) : (
                <>
                  {showAllPending ? (
                    <>
                      {pendingAssessments.map((assessment) => (
                        <div key={assessment._id} className="p-4 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-start justify-between mb-1">
                            <div className='flex gap-4 items-center text-center justify-center'>
                            <div className="font-medium text-gray-900">{assessment.patient?.name || 'Unknown'}</div>
                              <div className="text-xs text-gray-500">{timeAgo(assessment.updatedAt)}</div>
                            </div>
                            <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded ">Pending</span>
                          </div>
                          <div className="text-sm text-gray-500">{assessment.type || 'Assessment'}</div>
                         
                          <button
                            className="w-full text-sm bg-blue-600 text-white py-2 rounded hover:bg-blue-700 mt-2"
                            onClick={() => navigate(`/psy/patient/assessment/${assessment._id}`)}
                          >
                            View Full Assessment
                          </button>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* Show only first 3 assessments */}
                      {pendingAssessments.slice(0, 3).map((assessment) => (
                        <div key={assessment._id} className="p-4 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-start justify-between mb-1">
                            <div className='flex gap-4 items-center text-center justify-center'>
                            <div className="font-medium text-gray-900">{assessment.patient?.name || 'Unknown'}</div>
                              <div className="text-xs text-gray-500">{timeAgo(assessment.updatedAt)}</div>
                            </div>
                            <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded ">Pending</span>
                          </div>
                          <div className="text-sm text-gray-500">{assessment.type || 'Assessment'}</div>
                         
                          <button
                            className="w-full text-sm bg-blue-600 text-white py-2 rounded hover:bg-blue-700 mt-2"
                            onClick={() => navigate(`/psy/patient/assessment/${assessment._id}`)}
                          >
                            View Full Assessment
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

         
        </div>

        {/* Right Side - Urgent Cases & Quick Actions */}
        <div className="xl:col-span-2 space-y-6">
          {/* Urgent Cases */}
          
          {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Urgent Cases</h2>
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            
            <div className="space-y-3">
              {loadingUrgent ? (
                <>
                  <UrgentCaseSkeleton />
                  <UrgentCaseSkeleton />
                  <UrgentCaseSkeleton />
                </>
              ) : urgentCases.length === 0 ? (
                <div className="text-gray-500">No urgent cases.</div>
              ) : urgentCases.map((a) => (
                <div key={a._id} className="p-4 rounded-lg border border-red-100 bg-red-50">
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-sm font-medium text-gray-900">{a.patient?.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-500">{timeAgo(a.updatedAt)}</div>
                  </div>
                  <div className="text-red-600 text-xs mb-3 font-medium">
                    {a.riskFlags && a.riskFlags.length > 0 ? a.riskFlags[0] : 'High Risk'}
                  </div>
                  <button
                    className="w-full bg-white border border-red-100  text-red-600 text-sm font-semibold py-3 rounded transition-all"
                    onClick={() => navigate(`/psy/patient/assessment/${a._id}`)}
                  >
                    Review Assessment
                  </button>
                </div>
              ))}
            </div>
          </div> */}

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm  font-semibold text-gray-900 mb-4">Quick Actions</h2>

            <div className="grid grid-cols-1 gap-3">
              {/* <button className="p-4 border border-gray-200 hover:border-gray-300 rounded-lg text-center transition-all">
                <div className="flex justify-center mb-1 text-blue-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-800 font-medium">Add Clinical Note</span>
              </button> */}

              {/* <button className="p-4 border border-gray-200 hover:border-gray-300 rounded-lg text-center transition-all">
                <div className="flex justify-center mb-1 text-blue-600">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 4.5C8.4 4.5 5.5 7.4 5.5 11S8.4 17.5 12 17.5 18.5 14.6 18.5 11 15.6 4.5 12 4.5zm0 11c-1.1 0-2-.9-2-2h4c0 1.1-.9 2-2 2zm0-9c2.2 0 4 1.8 4 4H8c0-2.2 1.8-4 4-4z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-800 font-medium">View All Sessions</span>
              </button> */}

              <button 
                className="p-4 border border-gray-200 hover:border-gray-300 rounded-lg text-center transition-all"
                onClick={() => navigate('/psy/patients')}
              >
                <div className="flex justify-center mb-1 text-blue-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-800 font-medium">View My Patients</span>
              </button>

              {/* <button className="p-4 border border-gray-200 hover:border-gray-300 rounded-lg text-center transition-all">
                <div className="flex justify-center mb-1 text-blue-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-800 font-medium">Create Diagnosis</span>
              </button> */}
            </div>
          </div>
            {/* Referral Verification */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6">

              <ReferralVerification />
            </div>
        </div>
      </div>
   </div>
      <SessionDetailModal
        sessionId={selectedSessionId}
        open={showSessionModal}
        onClose={() => setShowSessionModal(false)}
      />
      
      <ConsultationCompletionForm
        session={selectedSessionForCompletion}
        open={showCompletionForm}
        onClose={() => {
          setShowCompletionForm(false);
          setSelectedSessionForCompletion(null);
        }}
        onSubmit={handleConsultationComplete}
      />
    </>
  );
};

export default PsychiatristDashboard;
